import type { RouteRecordRaw } from 'vue-router'

import { ROUTES } from './routeNames'
import auth from './middleware/auth'

import AuthenticatedLayout from '@dashboard/components/layouts/AuthenticatedLayout.vue'

import dashboardRoutes from '@dashboard/modules/dashboard/dashboardRoutes'
import revenueRoutes from '@dashboard/modules/revenue/revenueRoutes'
import settingsRoutes from '@dashboard/modules/settings/settingsRoutes'
import segmentRoutes from '@dashboard/modules/segment/segmentRoutes'
import flowRoutes from '@dashboard/modules/flow/flowRoutes'
import widgetRoutes from '@dashboard/modules/widget/widgetRoutes'
import offerRoutes from '@dashboard/modules/offer/offerRoutes'
import personalizationRoutes from '@dashboard/modules/personalization/personalizationRoutes'
import insightsRoutes from '@dashboard/modules/insights/insightsRoutes'
import { integrationTabFromLegacyQuery } from '@dashboard/modules/integration/integrationTabs'
import onboardingRoutes from '@dashboard/modules/onboarding/onboardingRoutes'
import templateRoutes from '@dashboard/modules/template/templateRoutes'
import splittestingRoutes from '@dashboard/modules/splittesting/splittestingRoutes'
import visitorsRoutes from '@dashboard/modules/visitors/visitorsRoutes'
import leadAlertsRoutes from '@dashboard/modules/leadAlerts/leadAlertsRoutes'
import adsRoutes from '@dashboard/modules/ads/adsRoutes'
import pagesRoutes from '@dashboard/modules/pages/pagesRoutes'
import { safeInternalRedirect } from './safeRedirect'
import { backendUrl } from '@dashboard/utils/backendUrl'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useNotificationStore } from '@dashboard/stores/notificationStore'

const routes: RouteRecordRaw[] = [
  {
    name: ROUTES.HOME,
    path: '/',
    component: { render: () => null },
    beforeEnter: () => {
      const authStore = useAuthStore()
      void authStore.postLogin({ ignoreRedirect: true })
    },
    meta: { middleware: [auth] }
  },
  {
    name: ROUTES.OAUTH_CONTINUE,
    path: '/oauth/continue',
    component: () => import('@dashboard/modules/user/pages/OAuthContinue.vue'),
    meta: { middleware: [auth] }
  },

  {
    name: ROUTES.LOGIN,
    path: '/login',
    component: () => import('@dashboard/modules/user/pages/Login.vue'),
    beforeEnter: (to) => {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) {
        return
      }
      // A param-less dashboard route becomes /undefined/dashboard and the auth
      // guard sends that back here. Resume a real in-app path, or the team home.
      const redirect = safeInternalRedirect(to.query.redirect)
      if (redirect) {
        return redirect
      }
      void authStore.postLogin()
      return false
    }
  },
  {
    // The personalization audit's login link returns here (`/login?redirect=/personalize-audit`). The session lives on
    // the backend host, so the backend sends a signed-in browser on to the audit.
    path: '/personalize-audit',
    component: { render: () => null },
    beforeEnter: () => {
      window.location.assign(backendUrl('/personalize-audit'))
      return false
    }
  },
  {
    // The audit's "Have us build this" (`?pid=&scan=`): the backend checks access, stores the scan and opens the
    // project's dashboard with Righty's build kickoff.
    path: '/personalize-audit/build',
    component: { render: () => null },
    beforeEnter: (to) => {
      window.location.assign(backendUrl(to.fullPath))
      return false
    }
  },
  {
    name: ROUTES.FORGOTPASSWORD,
    path: '/forgot-password',
    component: () => import('@dashboard/modules/user/pages/ForgotPassword.vue')
  },
  {
    name: ROUTES.RESETPASSWORD,
    path: '/reset-password',
    component: () => import('@dashboard/modules/user/pages/ResetPassword.vue')
  },
  {
    name: ROUTES.LOGOUT,
    path: '/logout',
    component: { render: () => null },
    // Reach it in-app (RouterLink), never with a full page load: only the page that sent
    // requests can abort them, and one finishing after the logout signs the browser back in
    // (see withoutOtherRequests). Login then loads fresh so nothing from the session stays in memory.
    beforeEnter: async (_to, from) => {
      if (await useAuthStore().logout()) {
        window.location.assign('/login')
        return false
      }
      useNotificationStore().addError('We couldn’t sign you out. Check your connection and try again.')
      return from.name ? false : { name: ROUTES.HOME }
    }
  },
  {
    name: ROUTES.REGISTER,
    path: '/register',
    alias: '/register/success',
    component: () => import('@dashboard/modules/user/pages/Register.vue'),
    beforeEnter: () => {
      const authStore = useAuthStore()
      if (authStore.isAuthenticated) {
        return { name: ROUTES.DASHBOARD }
      }
      return
    }
  },
  {
    name: ROUTES.AGENCY_SIGNUP,
    path: '/agencyOnboard/:agencySlug',
    component: () => import('@dashboard/modules/user/pages/AgencyRegister.vue'),
    beforeEnter: () => {
      const authStore = useAuthStore()
      if (authStore.isAuthenticated) {
        return { name: ROUTES.DASHBOARD }
      }
      return
    }
  },
  {
    name: ROUTES.ACCEPTINVITATION,
    path: '/accept',
    component: () => import('@dashboard/modules/user/pages/AcceptInvitation.vue')
  },
  {
    name: ROUTES.ACCEPT_ORGANIZATION_INVITATION,
    path: '/organization-invitations/:hash',
    component: () => import('@dashboard/modules/user/pages/AcceptOrganizationInvitation.vue')
  },
  {
    name: ROUTES.ACCEPT_PROJECT_HANDOFF,
    path: '/project-handoffs/:hash',
    component: () => import('@dashboard/modules/user/pages/AcceptProjectHandoff.vue')
  },
  {
    name: ROUTES.SETUPBLANKUSER,
    path: '/setup',
    component: () => import('@dashboard/modules/user/pages/SetupBlankUser.vue')
  },
  {
    path: '/:teamPid',
    component: AuthenticatedLayout,
    meta: { middleware: [auth] },
    children: [
      ...onboardingRoutes,
      ...dashboardRoutes,
      ...revenueRoutes,
      ...settingsRoutes,
      ...segmentRoutes,
      ...flowRoutes,
      ...widgetRoutes,
      ...offerRoutes,
      ...personalizationRoutes,
      ...insightsRoutes,
      ...templateRoutes,
      ...splittestingRoutes,
      ...visitorsRoutes,
      ...leadAlertsRoutes,
      ...adsRoutes,
      ...pagesRoutes,
      // Backward-compat redirects for routes moved under settings
      {
        path: 'integrations',
        redirect: (to) => ({
          name: ROUTES.INTEGRATIONS,
          params: { teamPid: to.params.teamPid },
          query: integrationTabFromLegacyQuery(to.query)
        })
      },
      {
        path: 'integrations/:integration',
        redirect: (to) => ({
          name: ROUTES.INTEGRATION,
          params: { teamPid: to.params.teamPid, integration: to.params.integration }
        })
      },
      {
        path: 'attribution',
        redirect: (to) => ({
          name: ROUTES.INTEGRATIONS,
          params: { teamPid: to.params.teamPid },
          hash: '#lead-attribution'
        })
      },
      {
        path: '/:teamPid/:pathMatch(.*)*',
        name: ROUTES.NOTFOUNDAUTH,
        component: () => import('@dashboard/components/pages/NotFound.vue')
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: ROUTES.NOTFOUND,
    component: () => import('@dashboard/components/pages/NotFound.vue')
  }
]

export default routes
